import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { Alert, Stack } from "@mui/material";
import { BorderColorOutlined, InfoOutlined } from "@mui/icons-material";
import PropTypes from "prop-types";

import Button from "../Button";
import Breadcrumbs from "../BreadCrumbs";
import { UIEmptyState, UIIcon } from "../ui";
import Modal from "../Modal";
import Avatar from "../Avatar";
import {
  renderTextEllipsis,
  renderWordEllipsis,
  textContainOnlySpaces,
  workHelperShift,
} from "../../utils/Helpers";
import {
  approveRequest,
  bulkApprovedRequestsRequest,
  updateEventRequest,
} from "../../store/Requests/actions";
import {
  getCitiesRequest,
  getLookupsRequest,
  getCountriesRequest,
  getSpeakingProficiencyRequest,
  getWorkingShiftRequest,
} from "../../store/Lookups/actions";
import * as lookupsApi from "../../network/apis/lookups";
import { inviteApplicantsRequest } from "../../store/Events/actions";
import {
  adminPermissionModules,
  genders,
  lookupsKeys,
  requestStatuses,
} from "../../utils/Constants";
import messages from "../../assets/locale/messages";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";

import {
  deleteLocationRequest,
  getLocationsRequest,
} from "../../store/Locations/actions";
import {
  createNoteRequest,
  deleteNoteRequest,
  getNoteRequest,
  updateNoteRequest,
} from "../../store/Notes/action";
import Note from "../Note";
import {
  initialFetchRequest,
  setApplicantRequestIds,
  setFiltersRequest,
  setFilterStatusRequest,
  setPageRequest,
  setRowsCountRequest,
  setSearchValueRequest,
} from "../../store/JoiningRequest/actions";
import Filters from "../Filter";
import { isAdmin, isSuperAdmin, isVisitor, isSupervisorManger } from "../../utils/Permissions";
import { useAdminModulePermission } from "../../utils/adminPortalHelpers";
import Tooltip from "../Tooltip";
import store from "../../store";
import { getPermissionRequest } from "../../store/Auth/actions";
import {
  UIBulkActions,
  UIButton,
  UIChip,
  UIActionModal,
  UIModal,
  UIRowActionsMenu,
  UIPaginationBar,
  UIRating,
  UIRadioGroup,
  UISelect,
  UITable,
} from "../ui";

import "./RequestsTable.scss";

const renderColumns = (
  shared,
  applicants,
  isApplicant,
  requests,
  events,
  isInviteApplicants,
  canAdd,
  canEdit,
  canDelete,
  handleAddNote,
  handleEditNote,
  handleDeleteNote,
  handleRow,
  handleEventView,
  approveRequestHandler,
  lang,
  onEditRole
) => {
  return [
    !isApplicant && {
      field: "id",
      headerName: shared.id,
      editable: false,
      filterable: false,
      hideable: false,
      hideShowIcon: true,
      disableColumnMenu: true,
      sortable: false,
      width: 105,
      renderCell: (params) => {
        return (
          <div className="MuiDataGrid-cellContent">
            {params.row?.id ? <span>{params.row.id}</span> : "-"}
          </div>
        );
      },
    },

    !isApplicant && {
      field: "user_id",
      headerName: requests.listRequests.applicantID,
      editable: false,
      filterable: false,
      hideable: false,
      hideShowIcon: true,
      disableColumnMenu: true,
      sortable: false,
      width: 120,
      renderCell: (params) => {
        return (
          <div className="MuiDataGrid-cellContent">
            {params.row?.user_id ? <span>{params.row.user_id}</span> : "-"}
          </div>
        );
      },
    },

    {
      field: "user image",
      headerName: shared.image,
      editable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      width: 70,
      renderCell: (params) => {
        return (
          <div className={`MuiDataGrid-cellContent`}>
            <Avatar
              imgSrcAlt="user profile"
              imgSrc={params.row?.image}
              name={params.row?.name}
              size={30}
            ></Avatar>
          </div>
        );
      },
    },

    !isApplicant && {
      field: "username",
      headerName: requests.listRequests.applicantName,
      editable: false,
      filterable: false,
      hideable: false,
      hideShowIcon: true,
      disableColumnMenu: true,
      sortable: false,
      width: 220,
      renderCell: (params) => {
        return (
          <div className={`MuiDataGrid-cellContent d-flex align-items-center`}>
            <div
              className={` ${
                (isInviteApplicants
                  ? !params.row.deleted_row
                  : !params.row.deleted_user_at) && "pointer"
              }`}
              onClick={() =>
                handleRow(
                  params.row.deleted_row,
                  params.row.deleted_user_at,
                  params.row.id,
                  params.row.user_id
                )
              }
            >
              {params.row?.name}
            </div>
          </div>
        );
      },
    },

    !isApplicant &&
      !isInviteApplicants && {
        field: "event_name",
        headerName: applicants.applicantRequsets.eventName,
        editable: false,
        filterable: false,
        hideable: false,
        hideShowIcon: true,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params) => {
          return (
            <div
              className="MuiDataGrid-cellContent"
              onClick={() => handleEventView(params.row.event_id)}
            >
              {params.row?.event_name
                ? renderWordEllipsis(params.row?.event_name)
                : "-"}
            </div>
          );
        },
      },

    !isApplicant && {
      field: "city_name",
      headerName: applicants.listApplicants.cityOfResidence,
      editable: false,
      filterable: false,
      hideable: false,
      hideSortIcons: false,
      disableColumnMenu: true,
      sortable: false,
      width: 90,
    },

    !isApplicant && {
      field: "gender_name",
      headerName: applicants.listApplicants.gender,
      editable: false,
      filterable: false,
      hideable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
      width: 90,
    },

    !isApplicant &&
      !isInviteApplicants && {
        field: "note",
        headerName: applicants.listApplicants.note,
        editable: false,
        filterable: false,
        hideable: false,
        hideSortIcons: true,
        disableColumnMenu: true,
        sortable: false,
      },

    !isApplicant &&
      !isInviteApplicants && {
        field: "shift",
        headerName: requests.listRequests.shift,
        editable: false,
        filterable: false,
        hideable: false,
        hideSortIcons: true,
        disableColumnMenu: true,
        sortable: false,
        width: 90,
      },

    !isApplicant && {
      field: "current_role",
      headerName: (
        <Tooltip
          key={1}
          title={<p>{shared.hint}</p>}
          content={
            <span>
              {shared.role}
              <span className="px-1">
                <InfoOutlined fontSize="small" />
              </span>
            </span>
          }
        />
      ),
      editable: false,
      filterable: false,
      hideable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            {(
              isInviteApplicants
                ? params.row.current_role?.name_en !== "Supervisor"
                : params.row.role?.name_en !== "Supervisor"
            ) ? (
              params.row.current_role && (
                <div className="d-flex align-items-center justify-content-between edit-role-wrapper">
                  <span className="MuiDataGrid-cellContent">
                    {params.row.current_role?.["name_" + lang] ||
                      params.row.current_role?.name}
                  </span>
                  <BorderColorOutlined
                    className="mx-2 pointer"
                    color={
                      isInviteApplicants
                        ? params.row.isRoleAcceptable
                          ? "primary"
                          : "error"
                        : "primary"
                    }
                    fontSize="14px"
                    onClick={() =>
                      onEditRole({
                        options: params.row.acceptable_role,
                        currentRole: params.row.current_role,
                        index: params.row.index,
                        applicantId: isInviteApplicants
                          ? params.row.id
                          : params.row.user_id,
                        applicantName: isInviteApplicants
                          ? params.row?._row[
                              lang === "en" ? "full_name_en" : "full_name_ar"
                            ]
                          : params.row.user?.[
                              lang === "en" ? "full_name_en" : "full_name_ar"
                            ],
                        currentShift:
                          params.row._row?.event_role_shift ??
                          params.row._row?.role?.event_role_shift,
                      })
                    }
                  />
                </div>
              )
            ) : (
              <span className="MuiDataGrid-cellContent">
                {params.row.current_role?.["name_" + lang] ||
                  params.row.current_role?.name}
              </span>
            )}
          </>
        );
      },
    },

    !isApplicant && {
      field: "rating",
      headerName: requests.listRequests.rating,
      editable: false,
      filterable: false,
      hideable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
        renderCell: (params) => {
          return (
            <div className="MuiDataGrid-cellContent d-flex">
              <UIRating value={params?.row.rating || 0} />
            </div>
          );
        },
    },

    !isInviteApplicants && {
      field: "nationality",
      headerName: applicants.listApplicants.nationality,
      editable: false,
      filterable: false,
      hideable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
    },

    !isApplicant &&
      !isInviteApplicants && {
        field: "profileStatus",
        headerName: shared.status,
        editable: false,
        filterable: false,
        hideable: false,
        hideSortIcons: true,
        disableColumnMenu: true,
        sortable: false,
        minWidth: 120,
        width: 120,
        renderCell: (params) => {
          return (
            <UIChip
              label={requests.listRequests[params.row.status]}
              shape="pill"
              sx={requestStatusChipSx(params.row.status)}
            />
          );
        },
      },

    !isInviteApplicants && {
      field: "action",
      headerName: applicants.applicantRequsets.actions,
      editable: false,
      filterable: false,
      hideable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      sortable: false,
      width: 90,
      minWidth: 80,
      align: "center",
      headerAlign: "center",
      cellClassName: "requests-list-container__actions-cell",
      renderCell: (params) => {
        const items = [];

        if (isSuperAdmin() || (isVisitor() && canEdit)) {
          items.push({
            key: "approve",
            label: shared.approve,
            disabled: params.row.status !== "pending",
            onClick: () => {
              const shiftFromRow =
                params.row._row?.event_role_shift ??
                params.row._row?.role?.event_role_shift;
              const selectedShift = shiftFromRow?.id
                ? { id: shiftFromRow.id, ...shiftFromRow }
                : null;
              approveRequestHandler(
                params.row.current_role,
                params.row.id,
                params.row.acceptable_role,
                params.row.user_gender,
                selectedShift
              );
            },
          });
        }

        if (
          ((isAdmin() && isSuperAdmin()) ||
            (isVisitor() && canAdd)) &&
          !params.row.note_info
        ) {
          items.push({
            key: "addNote",
            label: shared.addNote,
            onClick: () => handleAddNote(params.row.user_id),
          });
        }

        if (
          ((isAdmin() && isSuperAdmin()) ||
            (isVisitor() && canEdit)) &&
          !!params.row.note_info
        ) {
          items.push({
            key: "editNote",
            label: shared.editNote,
            onClick: () => handleEditNote(params.row.user_id),
          });
        }

        if (
          ((isAdmin() && isSuperAdmin()) ||
            (isVisitor() && canDelete)) &&
          !!params.row.note_info
        ) {
          items.push({
            key: "deleteNote",
            label: shared.deleteNote,
            danger: true,
            onClick: () => handleDeleteNote(params.row.user_id),
          });
        }

        return (
          <UIRowActionsMenu
            ariaLabel={applicants.applicantRequsets.actions}
            items={items}
          />
        );
      },
    },
  ];
};

const ratingOptions = [
  {
    id: 1,
    name: "1",
    value: 1,
  },
  {
    id: 2,
    name: "2",
    value: 2,
  },
  {
    id: 3,
    name: "3",
    value: 3,
  },
  {
    id: 4,
    name: "4",
    value: 4,
  },
  {
    id: 5,
    name: "5",
    value: 5,
  },
];

const requestStatusChipSx = (status) => {
  const normalized = String(status || "").toLowerCase();
  const base = {
    maxWidth: "none",
    "& .MuiChip-label": {
      overflow: "visible",
      textOverflow: "clip",
      whiteSpace: "nowrap",
    },
  };

  if (normalized === requestStatuses.accepted || normalized === "approved") {
    return {
      ...base,
      color: "var(--color-success-600)",
      backgroundColor: "var(--color-success-0)",
    };
  }

  if (
    normalized === requestStatuses.cancelled ||
    normalized === requestStatuses.autoCancelled
  ) {
    return {
      ...base,
      color: "var(--color-error-600)",
      backgroundColor: "var(--color-error-0)",
    };
  }

  return {
    ...base,
    color: "var(--color-neutral-500)",
    backgroundColor: "var(--color-neutral-100)",
  };
};

const RequestsTable = ({
  isApplicant,
  isInviteApplicants,
  breadcrumbsList,
  userId,
  searchPlaceholder,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();

  const { id: user_id } = store.getState().auth.login.role;
  const { canView, canAdd, canEdit, canDelete } = useAdminModulePermission(
    adminPermissionModules.joiningEvents,
  );

  const lang = useSelector((state) => state.locale.lang);
  const { requests, events, shared, applicants } = messages[lang];

  const meta = useSelector((state) =>
    isInviteApplicants
      ? state.applicants.eligibleApplicants.meta
      : state.requests.requestsList.meta
  );
  const list = useSelector((state) =>
    isInviteApplicants
      ? state.applicants.eligibleApplicants.list
      : state.requests.requestsList.list
  );

  const { roles, languages } = useSelector((state) => state.lookups.general);
  const { cities, countries, speakingProficiency, workingShift } = useSelector(
    (state) => state.lookups
  );
  const filters = useSelector((state) => state.joiningRequest);

  const location = useSelector((state) => state.locations.list);
  const note = useSelector((state) => state.note.list);
  const isLoading = useSelector((state) => state.loader);
  const [q, setQuery] = useState("");
  const [openWarningApproveModal, setOpenWarningApproveModal] = useState(false);

  const [singleRole, setSingleRole] = useState(null);
  const [singleShift, setSingleShift] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [openRoleModal, setOpenRoleModal] = useState(false);
  const [userName, setUserName] = useState(null);
  const [activeUserId, setActiveUserId] = useState(null);
  const [rolesOptions, setRolesOptions] = useState([]);
  const [originalRole, setOriginalRole] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [modalShifts, setModalShifts] = useState([]);
  const [roleIndex, setRoleIndex] = useState(null);
  const [isRoleSelected, setIsRoleSelected] = useState(false);
  const [autoSelect, setAutoSelect] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectValue, setSelectValue] = useState(null);
  const [otherValue, setOtherValue] = useState("");
  const [validate, setValidate] = useState(false);
  const [selectedApplicantId, setSelectedApplicantId] = useState(null);
  const [approvedModalClose, setApprovedModalClose] = useState(false);
  const [eventShift, setEventShift] = useState([]);
  const [rowsTable, setRowsTable] = useState([]);
  const requestInitialRef = useRef(false);

  const statusOptions = [
    { id: 1, name: requests.listRequests.all, value: null },
    {
      id: 2,
      name: requests.listRequests.approved,
      value: requestStatuses.accepted,
    },
    {
      id: 3,
      name: requests.listRequests.pending,
      value: requestStatuses.notAccepted,
    },
    {
      id: 4,
      name: requests.listRequests.cancelled,
      value: requestStatuses.cancelled,
    },
    {
      id: 5,
      name: requests.listRequests.auto_cancelled,
      value: requestStatuses.autoCancelled,
    },
  ];
  const genderOptions = [
    {
      id: 1,
      name: shared.male,
      value: genders.male,
    },
    {
      id: 2,
      name: shared.female,
      value: genders.female,
    },
    {
      id: 3,
      name: shared.notProvided,
      value: genders.notProvided,
    },
  ];

  const handleApprovedCloseModal = () => {
    setApprovedModalClose(false);
  };
  const handleApprovedOpenModal = () => setApprovedModalClose(true);

  useEffect(() => {
    const updateShifts = workingShift.map((item) => ({
      ...item,
      name: item.shift_type
        .split("_")
        .map((w) => w.charAt(0).toString().toUpperCase() + w.slice(1))
        .join(" "),
    }));
    setEventShift(updateShifts);
    handleFilterRequest({ filterShift: null });
  }, [filters.filterRole, workingShift]);

  useEffect(() => {
    const _location = location.find((item) => item.id === note.location_id);
    setSelectValue(
      note.custom_location
        ? "other"
        : _location?.name || shared.selectedLocation
    );
    setOtherValue(note.custom_location || "");
  }, [note]);

  useEffect(() => {
    if (!isSuperAdmin() && !isAdmin() && !isSupervisorManger()) {
      dispatch(getPermissionRequest({ id: user_id }));
    }
  }, [lang]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectValue) return;
    const location_id = location.find((item) => item?.name === selectValue);
    handleModalClose();
    setSelectValue(shared.selectedLocation);
    setOtherValue("");
    dispatch(
      note?.custom_location
        ? updateNoteRequest({
            id: selectedApplicantId,
            data: {
              custom_location: otherValue,
              location_id: location_id?.id,
            },
          })
        : createNoteRequest({
            id: selectedApplicantId,
            data: {
              custom_location: otherValue,
              location_id: location_id?.id,
            },
          })
    );
  };

  const handleEditNote = (applicantId) => {
    dispatch(getNoteRequest({ id: applicantId }));
    setSelectedApplicantId(applicantId);
    setIsNoteModalOpen(true);
  };
  const handleDeleteLocation = (id) => {
    dispatch(deleteLocationRequest({ id }));
    history.push(ROUTE_PATHS["joining-festivities"]);
    setIsNoteModalOpen(false);
  };

  const handleAddNote = (applicantId) => {
    setSelectedApplicantId(applicantId);
    setIsNoteModalOpen(true);
    setSelectValue(shared.selectedLocation);
    setOtherValue("");
  };

  const handleModalClose = () => setIsNoteModalOpen(false);

  const handleDeleteNote = (applicantId) => {
    setSelectedApplicantId(applicantId);
    dispatch(deleteNoteRequest({ id: applicantId }));
    dispatch(initialFetchRequest({ id }));
  };
  const handleRow = (deleted_row, deleted_user_at, id, user_id) => {
    if (isVisitor() && canView) {
      if (isInviteApplicants ? !deleted_row : !deleted_user_at) {
        history.push(
          ROUTE_PATHS.viewRequestsApplicant.replace(
            ":id",
            isInviteApplicants ? id : user_id
          )
        );
      }
    } else {
      if (
        (isSuperAdmin() || isAdmin()) && isInviteApplicants
          ? !deleted_row
          : !deleted_user_at
      ) {
        history.push(
          ROUTE_PATHS.viewRequestsApplicant.replace(
            ":id",
            isInviteApplicants ? id : user_id
          )
        );
      }
    }
  };

  const handleEventView = (event_id) => {
    history.push(ROUTE_PATHS.viewEvent.replace(":id", event_id));
  };

  useEffect(() => {
    if (openRoleModal) return;
    const allRoles = list?.map((applicant) => {
      const previouslySelectedApplicant = selectedApplicants.find(
        (selected) => selected.user_id === applicant.id
      );
      return isInviteApplicants && previouslySelectedApplicant
        ? previouslySelectedApplicant.role
        : applicant.role;
    });
    setSelectedRoles(allRoles);
  }, [!!list?.length, list]);

  const approveRequestHandler = (
    selectedRole,
    requestId,
    acceptableRoles,
    gender,
    selectedShiftParam
  ) => {
    const selectedRoleData = acceptableRoles?.find(
      (role) => role.role?.id === selectedRole.id
    );

    if (
      (selectedRoleData?.maximum_number_of_females <=
        selectedRoleData?.number_of_approved_female_requests &&
        gender == genders.female) ||
      (selectedRoleData?.maximum_number_of_males <=
        selectedRoleData?.number_of_approved_male_requests &&
        gender == genders.male) ||
      (selectedRoleData?.maximum_number_of_males <=
        selectedRoleData?.number_of_approved_male_requests &&
        selectedRoleData?.maximum_number_of_females <=
          selectedRoleData?.number_of_approved_female_requests &&
        gender == genders.notProvided)
    ) {
      setOpenWarningApproveModal(true);
      setRequestId(requestId);
      setSingleRole(selectedRole);
      setSingleShift(selectedShiftParam);
    } else {
      onApproveRequest(selectedRole, requestId, selectedShiftParam);
    }
  };

  const onApproveRequest = (selectedRole, requestId, selectedShiftParam) => {
    let params = {
      role_id: getRoleId(selectedRole),
      event_role_shift_id: selectedShiftParam?.id ?? null,
    };
    dispatch(
      approveRequest({
        data: params,
        id: requestId,
        listParams: isApplicant
          ? {
              id,
              params: {
                page: filters.page,
                items: 10,
                user_id: userId,
                event_name: filters.searchValue,
                status: "pending",
                role_id: filters.filterRole?.id,
                order_by_created_at: "desc",
                responseType: "requests",
              },
            }
          : {
              id,
              params: {
                page: filters.page,
                items: 10,
                event_and_user_name: filters.searchValue,
                status: filters.filterRequestStatus?.value ?? "approved",
                role_id: filters.filterRole?.id,
                min_user_rating: filters.filterRating?.value,
                max_user_rating: filters.filterRating?.value
                  ? filters.filterRating?.value + 0.99
                  : null,
                order_by_created_at: "asc",
                responseType: "requests",
                city_id: filters.filterCity?.id,
              },
            },
      })
    );
    handlePageChange(1);
    handleCloseWarningApproveModal();
  };

  const handleCloseWarningApproveModal = () => {
    setOpenWarningApproveModal(false);
    setSingleShift(null);
  };

  const handlePageChange = (newPage) => {
    dispatch(setPageRequest(newPage));
  };

  const handleRowsRequestChange = (rows) => {
    dispatch(setRowsCountRequest(rows));
    dispatch(setPageRequest(1));
  };

  const handleFilterRequest = (newFilter) => {
    if (list.length === 0) {
      dispatch(setPageRequest(1));
    }
    dispatch(setFiltersRequest({ ...filters, ...newFilter }));
  };
  const handleFilterStatus = (status) => {
    dispatch(setFilterStatusRequest({ filterRequestStatus: status }));
  };

  useEffect(() => {
    dispatch(initialFetchRequest({ id }));
  }, [filters, note]);

  useEffect(() => {
    if ((q.length >= 3 || q.length === 0) && !textContainOnlySpaces(q)) {
      dispatch(setSearchValueRequest(q));
      handlePageChange(1);
    }
  }, [q]);

  useEffect(() => {
    if (requestInitialRef.current) return;
    dispatch(initialFetchRequest({ id }));
    dispatch(getLocationsRequest());
    dispatch(getLookupsRequest([lookupsKeys.languages, lookupsKeys.roles]));
    dispatch(getCountriesRequest({ page: -1 }));
    dispatch(getSpeakingProficiencyRequest());
    dispatch(
      getCitiesRequest({
        page: -1,
        order_by_name_en: lang === "en" ? "asc" : null,
        order_by_name_ar: lang === "ar" ? "asc" : null,
      })
    );
    requestInitialRef.current = true;
  }, []);

  useEffect(() => {
    if (filters.filterRole?.id) {
      dispatch(
        getWorkingShiftRequest({
          role_id: filters.filterRole?.id ?? null,
          event_id: id,
        })
      );
    } else {
      handleFilterRequest({ filterShift: null });
    }
  }, [filters.filterRole]);

  const updatedSpeaking = speakingProficiency.map((item) => ({
    ...item,
    name: item.label,
  }));


  const fetchShiftsForRole = async (roleObj) => {
    const roleId = roleObj?.id ?? roleObj?.role?.id;
    if (!roleId || !id) return;
    setModalShifts([]);
    try {
      const res = await lookupsApi.getWorkingShift({ role_id: roleId, event_id: id });
      setModalShifts(res?.data?.data ?? []);
    } catch {
      setModalShifts([]);
    }
  };

  const onEditRole = ({
    options,
    currentRole,
    applicantId,
    index,
    applicantName,
    currentShift: currentShiftFromRow,
  }) => {
    const row = list?.[index];
    const shiftFromRow =
      currentShiftFromRow ?? row?.event_role_shift ?? row?.role?.event_role_shift;
    const shift =
      shiftFromRow?.id
        ? { id: shiftFromRow.id, name: null, ...shiftFromRow }
        : null;
    setRolesOptions(options);
    setOriginalRole(currentRole);
    setSelectedShift(shift);
    setRoleIndex(index);
    setUserName(applicantName);
    setActiveUserId(applicantId);
    setOpenRoleModal(true);
    fetchShiftsForRole(currentRole, index);
  };

  const handleSelectRole = (value, index) => {
    if (value) {
      const selectedRolesCopy = [...selectedRoles];
      selectedRolesCopy[index] = value;
      setSelectedRoles(selectedRolesCopy);
      setSelectedShift(null);
      fetchShiftsForRole(value, index);
    }
  };

  const handleCloseRoleModal = (selectedRole) => {
    handleSelectRole(selectedRole || originalRole, roleIndex);
    setOpenRoleModal(false);
    setUserName(null);
    setActiveUserId(null);
    setOriginalRole(null);
    setRolesOptions([]);
    setRoleIndex(null);
    setSelectedShift(null);
    setModalShifts([]);
    setIsRoleSelected(false);
    setAutoSelect(false);
  };

  const getRoleId = (r) => r?.role?.id ?? r?.id;

  const renderRolesOptions = () => {
    const radioList =
      rolesOptions?.map((option) => {
        const role = option?.role || option;
        return { value: String(role?.id ?? ""), label: role?.name };
      }) ?? [];

    const currentRoleId = getRoleId(selectedRoles?.[roleIndex]);
    const currentRoleValue =
      currentRoleId != null ? String(currentRoleId) : "";
    const selectedRole = radioList.find(
      (item) => item.value == getRoleId(selectedRoles?.[roleIndex])
    );
    if (isRoleSelected != !!selectedRole) setIsRoleSelected(!!selectedRole);

    const shiftOptions = (modalShifts ?? []).map((shift) => ({
      id: shift.id,
      name:
        shared.shiftTypes?.[shift.shift_type] ||
        workHelperShift(shift.shift_type) ||
        shift.shift_type,
    }));

    const shiftRequired = shiftOptions.length > 0;
    const selectedShiftValue = selectedShift
      ? {
          id: selectedShift.id,
          name:
            selectedShift.name ??
            (shared.shiftTypes?.[selectedShift.shift_type] ||
              workHelperShift(selectedShift.shift_type) ||
              selectedShift.shift_type),
        }
      : null;

    return (
      <>
        <UIRadioGroup
          name="user-role"
          value={currentRoleValue}
          options={radioList}
          onChange={(value) => {
            const selectedRole = rolesOptions.find(
              (elt) => String(getRoleId(elt)) === value
            );
            handleSelectRole(selectedRole, roleIndex);
          }}
        />

        <UISelect
          name="role-shift-select"
          label={requests.listRequests.shift}
          required={shiftRequired}
          error={shiftRequired && !selectedShift?.id}
          options={shiftOptions}
          value={selectedShiftValue}
          onChange={(value) => {
            if (!value) {
              setSelectedShift(null);
              return;
            }
            const fullShift = modalShifts.find((shift) => shift.id === value.id);
            setSelectedShift(
              fullShift
                ? {
                    id: fullShift.id,
                    name: value.name,
                    shift_type: fullShift.shift_type,
                  }
                : { id: value.id, name: value.name }
            );
          }}
          disabled={!currentRoleValue || shiftOptions.length === 0}
        />
      </>
    );
  };

  const handleConfirmRole = () => {
    const requestData = list[roleIndex];
    if (isInviteApplicants) {
      handleChangeSelectedApplicantRole();
      handleCloseRoleModal(selectedRoles[roleIndex]);
    } else {
      if (
        requestData.status === "approved" ||
        requestData.status === "accepted"
      ) {
        dispatch(
          updateEventRequest({
            data: {
              role_id: getRoleId(selectedRoles[roleIndex]),
              event_role_shift_id: selectedShift?.id ?? null,
            },
            id: requestData.id,
            listParams: isApplicant
              ? {
                  id,
                  params: {
                    page: filters.page,
                    items: 10,
                    user_id: userId,
                    event_name: filters.searchValue,
                    status: filters.filterRequestStatus?.value ?? "approved",
                    role_id: filters.filterRole?.id,
                    order_by_created_at: "asc",
                    responseType: "requests",
                  },
                }
              : {
                  id,
                  params: {
                    page: filters.page,
                    items: 10,
                    event_and_user_name: filters.searchValue,
                    status: filters.filterRequestStatus?.value ?? "approved",
                    role_id: filters.filterRole?.id,
                    order_by_created_at: "asc",
                  },
                },
          })
        );
        handleCloseRoleModal();
      } else {
        approveRequestHandler(
          selectedRoles[roleIndex],
          requestData.id,
          requestData.acceptable_roles,
          requestData.user?.gender,
          selectedShift
        );
        handleCloseRoleModal();
      }
    }
  };

  const handleChangeSelectedApplicantRole = () => {
    const selectedApplicant = selectedApplicants.find(
      (applicant) => applicant.user_id === activeUserId
    );
    const selectedApplicantIndex = selectedApplicants.findIndex(
      (applicant) => applicant.user_id === activeUserId
    );
    if (selectedApplicant) {
      const copySelectedApplicantsList = [...selectedApplicants];
      copySelectedApplicantsList.splice(selectedApplicantIndex, 1, {
        ...selectedApplicant,
        role: selectedRoles[roleIndex],
      });
      setSelectedApplicants(copySelectedApplicantsList);
    } else {
      if (autoSelect) handleSelectApplicant(true, activeUserId, roleIndex);
    }
  };

  const handleSelectApplicant = (isSelected, applicantId, applicantIndex) => {
    if (isSelected) {
      const applicantSelectedRole = selectedRoles[applicantIndex];
      setSelectedApplicants([
        ...selectedApplicants,
        {
          user_id: applicantId,
          role: applicantSelectedRole,
        },
      ]);
    } else {
      setSelectedApplicants(
        selectedApplicants.filter(
          (applicant) => applicant.user_id !== applicantId
        )
      );
    }
  };

  const handleInviteApplicants = () => {
    const invitations = selectedApplicants
      .filter(({ role }) => role)
      .map(({ user_id, role }) => ({
        user_id,
        role_id: role.id,
      }));
    dispatch(
      inviteApplicantsRequest({
        event_id: +id,
        invitations,
      })
    );
  };

  const handleSelectionRequestChange = (newCheckSelection) => {
    const previousSelection = filters.selectedApplicantRequestIds || [];

    const addedSelections = newCheckSelection.filter(
      (id) => !previousSelection.includes(id)
    );
    const removedSelections = previousSelection.filter(
      (id) => !newCheckSelection.includes(id)
    );
    let updatedSelectionRequest;
    if (addedSelections.length > 0) {
      updatedSelectionRequest = [...previousSelection, ...addedSelections];
    } else if (removedSelections.length > 0) {
      updatedSelectionRequest = previousSelection.filter(
        (id) => !removedSelections.includes(id)
      );
    } else {
      updatedSelectionRequest = previousSelection;
    }
    updatedSelectionRequest = Array.from(new Set(updatedSelectionRequest));
    dispatch(setApplicantRequestIds(updatedSelectionRequest));
  };

  const handleApprovedBulkRequest = () => {
    dispatch(
      bulkApprovedRequestsRequest({
        event_request_ids: filters.selectedApplicantRequestIds,
        event_id: +id,
        listParams: isApplicant
          ? {
              id,
              params: {
                page: filters.page,
                items: 10,
                user_id: userId,
                event_name: filters.searchValue,
                status: "pending",
                role_id: filters.filterRole?.id,
                order_by_created_at: "desc",
                responseType: "requests",
              },
            }
          : {
              id,
              params: {
                page: filters.page,
                items: 10,
                event_and_user_name: filters.searchValue,
                status: filters.filterRequestStatus?.value ?? "pending",
                role_id: filters.filterRole?.id,
                min_user_rating: filters.filterRating?.value,
                max_user_rating: filters.filterRating?.value
                  ? filters.filterRating?.value + 0.99
                  : null,
                order_by_created_at: "asc",
                responseType: "requests",
                city_id: filters.filterCity?.id,
              },
            },
      })
    );
    handleApprovedCloseModal();
  };

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("applicantsVisibleCols");
    };
  }, []);

  useEffect(() => {
    setRowsTable(
      list.map((row, i) => ({
        id: row.id,
        name: (
          isInviteApplicants
            ? row["full_name_" + lang]
            : row.user?.["full_name_" + lang]
        )
          ? renderTextEllipsis(
              isInviteApplicants
                ? row["full_name_" + lang]
                : row.user?.["full_name_" + lang]
            )
          : "-",
        image: isInviteApplicants ? row.image : row.user?.image,
        event_name: row.event?.name,
        event_id: row.event?.id,
        city_name:
          (isInviteApplicants ? row.city?.name : row.user.city?.name) || "-",
        gender_name: (isInviteApplicants ? row.gender : row.user.gender)
          ? shared.genderOptions[
              isInviteApplicants ? row.gender : row.user?.gender
            ]
          : "-",
        note:
          row.user.note?.custom_location ||
          row.user.note?.location?.name ||
          "-",
        shift:
          shared.shiftTypes?.[row?.event_role_shift?.shift_type] ||
          workHelperShift(row?.event_role_shift?.shift_type) ||
          "-",
        rating: isInviteApplicants
          ? +row.rating
          : +row.user?.rating?.toFixed(2),
        nationality: row.user?.nationality?.name || "-",
        status: row?.status,
        note_info: row.user?.note,
        user_id: row.user?.id,
        deleted_row: row.deleted_at,
        deleted_user_at: row.user?.deleted_at,
        acceptable_role: row?.acceptable_roles,
        current_role: selectedRoles[i] || row.role,
        isRoleAcceptable: !!row.acceptable_role?.find(
          (role) => role?.id == (selectedRoles[i]?.id || row.role?.id)
        ),
        user_gender: row.user?.gender,
        index: i,
        user: row.user,
        _row: row,
      }))
    );
    const currentSelected = filters.selectedApplicantIds || [];
    const visibleIds = list.map((row) => row.id);
    const selectedButNotVisible = currentSelected.filter(
      (id) => !visibleIds.includes(id)
    );

    if (selectedButNotVisible.length > 0) {
      dispatch(setApplicantRequestIds([...currentSelected]));
    }
  }, [list, selectedRoles, lang, shared]);

  const columns = useMemo(() => {
    return renderColumns(
      shared,
      applicants,
      isApplicant,
      requests,
      events,
      isInviteApplicants,
      canAdd,
      canEdit,
      canDelete,
      handleAddNote,
      handleEditNote,
      handleDeleteNote,
      handleRow,
      handleEventView,
      approveRequestHandler,
      lang,
      onEditRole
    ).filter(Boolean);
  }, [applicants, lang, shared, requests, events, canAdd, canEdit, canDelete]);

  const paginationTotal = meta?.count || 0;
  const paginationCurrentPage = meta?.currentPage || filters.page || 1;
  const paginationPageSize = filters.rowsCount || 10;
  const paginationFrom =
    paginationTotal === 0
      ? 0
      : (paginationCurrentPage - 1) * paginationPageSize + 1;
  const paginationTo = Math.min(
    paginationCurrentPage * paginationPageSize,
    paginationTotal
  );
  const paginationPages =
    meta?.pages || Math.ceil(paginationTotal / paginationPageSize) || 1;
  const paginationRangeTemplate =
    events.viewEvents.applicantsTab.joiningRequests.rangeTemplate;

  const renderRequestsList = () => {
    if (!isLoading && !list?.length) {
      return (
        <UIEmptyState
          classNames={{
            root: "requests-list-container__empty",
            iconCircle: "requests-list-container__empty-icon-circle",
          }}
          title={
            isInviteApplicants
              ? applicants.listApplicants.noDataDescription
              : requests.listRequests.placeholder
          }
          icon={<UIIcon name="applicantsEmpty" width={40} height={40} />}
        />
      );
    } else {
      return (
        <>
          {isInviteApplicants && (
            <div className="selected-applicants-count text-end">
              <p className="mb-0">
                <span
                  className={`text-light-grey ${
                    lang === "en" ? "me-2" : "ms-2"
                  }`}
                >
                  {shared.selected}
                </span>
                <span
                  className={`${
                    selectedApplicants?.length > 50
                      ? "text-warning"
                      : "text-primary-blue"
                  } fweight-500`}
                >
                  {selectedApplicants?.length}
                </span>
                <span className="text-light-grey mx-2">{shared.outOf}</span>
                <span className="text-primary-blue fweight-500">{50}</span>
              </p>
            </div>
          )}
          {filters.selectedApplicantRequestIds.length > 0 && (
            <UIBulkActions
              className="requests-list-container__bulk-actions"
              selectedCount={filters.selectedApplicantRequestIds.length}
              selectedLabel={events.viewEvents.applicantsTab.joiningRequests.selectedCount.replace(
                "{count}",
                filters.selectedApplicantRequestIds.length,
              )}
              actions={
                isSuperAdmin() || (isVisitor() && canEdit)
                  ? [
                      {
                        key: "approveRequests",
                        label: `${requests.listRequests.approvedRequest} (${filters.selectedApplicantRequestIds.length})`,
                        disabled: filters.filterRequestStatus?.value
                          ? filters.filterRequestStatus.value !==
                            requestStatuses?.notAccepted
                          : false,
                        onClick: handleApprovedOpenModal,
                      },
                    ]
                  : []
              }
            />
          )}
          <UITable
            rows={rowsTable}
            columns={columns}
            checkboxSelection
            onSelectionModelChange={handleSelectionRequestChange}
            keepNonExistentRowsSelected
            selectionModel={filters.selectedApplicantRequestIds.filter((id) =>
              rowsTable.some((row) => row.id === id)
            )}
            contentSized
            hideFooter
            disableSelectionOnClick
          />
          {paginationTotal > 0 && (
            <UIPaginationBar
              currentPage={paginationCurrentPage}
              pages={paginationPages}
              pageSize={paginationPageSize}
              rangeTemplate={paginationRangeTemplate}
              rangeFrom={paginationFrom}
              rangeTo={paginationTo}
              rangeTotal={paginationTotal}
              rowsPerPageLabel={shared.numberOfRows}
              onPageChange={handlePageChange}
              onPageSizeChange={handleRowsRequestChange}
              className="requests-list-container__pagination"
            />
          )}

          {isInviteApplicants && (
            <div className="d-flex justify-content-end my-3">
              <div className="d-flex justify-content-end align-items-center invite-btns-container">
                <Button
                  type="button"
                  block
                  label={shared.invite}
                  labelClass="px-4"
                  disabled={
                    selectedApplicants?.length > 50 ||
                    selectedApplicants?.length === 0
                  }
                  className="mx-2 continue-btn"
                  onClick={handleInviteApplicants}
                />
                <Button
                  onClick={() => {
                    history.push(ROUTE_PATHS.viewEvent.replace(":id", id));
                  }}
                  type="button"
                  block
                  label={shared.cancel}
                  labelClass="px-4"
                  outlined
                  className="mx-2 cancel-btn"
                />
              </div>
            </div>
          )}
          {openRoleModal && (
            <UIActionModal
              open={openRoleModal}
              onClose={() => handleCloseRoleModal()}
              title={`${requests.listRequests.changeRoleTitle} ${userName}`}
              maxWidth="sm"
              showIcon={false}
              PaperProps={{ className: "role-select-modal" }}
              actions={
                <UIActionModal.Actions
                  cancelLabel={shared.cancel}
                  confirmLabel={
                    isInviteApplicants ? shared.save : shared.approve
                  }
                  confirmDisabled={
                    !isRoleSelected ||
                    (modalShifts?.length > 0 &&
                      (!selectedShift ||
                        !modalShifts.some((s) => s.id == selectedShift?.id)))
                  }
                  onCancel={() => handleCloseRoleModal()}
                  onConfirm={handleConfirmRole}
                />
              }
            >
              {renderRolesOptions()}
            </UIActionModal>
          )}
          {openWarningApproveModal && (
            <Modal
              open={openWarningApproveModal}
              handleClose={handleCloseWarningApproveModal}
              title={requests.listRequests.warningTitle}
              content={
                <Alert severity="warning" className="fsize-16">
                  {requests.listRequests.warningMessage}
                </Alert>
              }
              isContentText={false}
              confirmBtnTxt={shared.approve}
              maxWidth="xs"
              cancelBtnTxt={shared.cancel}
              handleConfirm={() =>
                onApproveRequest(singleRole, requestId, singleShift)
              }
              handleCancel={handleCloseWarningApproveModal}
              modalClass="warning-modal"
              actionsClass="container"
              confirmBtnClass="btn-secondary text-white"
            />
          )}
        </>
      );
    }
  };

  return (
    <div
      className={`conatiner requests-list-container ${
        isInviteApplicants && "invite-applicants-list-container"
      }`}
    >
      <div className="page-title-margin">
        <Breadcrumbs list={breadcrumbsList} BreadCrumbHasBackIcon={false} />
      </div>
      <Filters
        classNames={{
          base: "",
          search: "mb-3 mb-md-0",
          filter: "",
        }}
        searchInput={{
          placeholder: searchPlaceholder,
          name: "search",
          value: q,
          onChange: (value) => setQuery(value),
        }}
        filters={
          isApplicant
            ? [
                {
                  type: "filter",
                  name: "role",
                  placeholder: shared.role,
                  value: filters.filterRole,
                  options: roles,
                  onChange: (value) =>
                    handleFilterRequest({ filterRole: value }),
                },
              ]
            : isInviteApplicants
            ? [
                {
                  type: "filter",
                  name: "rating",
                  placeholder: requests.listRequests.rating,
                  value: filters.filterRating,
                  options: ratingOptions,
                  isOptionsElt: true,
                  onChange: (value) =>
                    handleFilterRequest({ filterRating: value }),
                },
                {
                  type: "filter",
                  name: "gender",
                  placeholder: events?.viewEvents?.applicantsTab?.gender,
                  value: filters.filterGender,
                  options: genderOptions,
                  onChange: (value) =>
                    handleFilterRequest({ filterGender: value }),
                },
                {
                  type: "filter",
                  name: "role",
                  placeholder: events?.viewEvents?.applicantsTab?.role,
                  value: filters.filterRole,
                  options: roles,
                  onChange: (value) =>
                    handleFilterRequest({ filterRole: value }),
                },
                {
                  type: "filter",
                  name: "city",
                  placeholder: requests.listRequests.cityOfResidence,
                  value: filters.filterCity,
                  options: cities,
                  onChange: (value) =>
                    handleFilterRequest({ filterCity: value }),
                },
              ]
            : [
                {
                  type: "filter",
                  name: "status",
                  placeholder: requests.listRequests.status,
                  value: filters.filterRequestStatus,
                  options: statusOptions,
                  onChange: (value) => handleFilterStatus(value),
                },
                {
                  type: "filter",
                  name: "rating",
                  placeholder: requests.listRequests.rating,
                  value: filters.filterRating,
                  options: ratingOptions,
                  isOptionsElt: true,
                  onChange: (value) =>
                    handleFilterRequest({ filterRating: value }),
                },
                {
                  type: "filter",
                  name: "gender",
                  placeholder: events.viewEvents.applicantsTab.gender,
                  value: filters.filterGender,
                  options: genderOptions,
                  onChange: (value) =>
                    handleFilterRequest({ filterGender: value }),
                },
                {
                  type: "filter",
                  name: "role",
                  placeholder: events.viewEvents.applicantsTab.role,
                  value: filters.filterRole,
                  options: roles,
                  onChange: (value) =>
                    handleFilterRequest({ filterRole: value }),
                },
                {
                  type: "filter",
                  name: "city",
                  placeholder: requests.listRequests.cityOfResidence,
                  value: filters.filterCity,
                  options: cities,
                  onChange: (value) =>
                    handleFilterRequest({ filterCity: value }),
                },
                {
                  type: "filter",
                  name: "nationality",
                  placeholder: requests.listRequests.nationality,
                  value: filters.filterNationality,
                  options: countries,
                  onChange: (value) =>
                    handleFilterRequest({ filterNationality: value }),
                },
                {
                  type: "filter",
                  name: "language",
                  placeholder: requests.listRequests.language,
                  value: filters.filterLanguage,
                  options: languages,
                  onChange: (value) =>
                    handleFilterRequest({ filterLanguage: value }),
                },
                {
                  type: "filter",
                  name: "Speaking Proficiency",
                  placeholder: requests.listRequests.speaking,
                  value: filters.filterSpeaking,
                  options: updatedSpeaking,
                  onChange: (value) =>
                    handleFilterRequest({ filterSpeaking: value }),
                },
                filters.filterRole && {
                  type: "filter",
                  name: "shifts",
                  placeholder: events.viewEvents.applicantsTab.shift,
                  value: filters.filterShift,
                  options: eventShift,
                  onChange: (value) =>
                    handleFilterRequest({ filterShift: value }),
                },
              ]
        }
      />
      <div className="requests-list-container__content d-flex flex-column justify-content-between">
        {renderRequestsList()}
      </div>
      <UIModal
        open={isNoteModalOpen}
        onClose={handleModalClose}
        title={shared.addYourNote}
        maxWidth="sm"
        PaperProps={{ className: "applicant-note-modal" }}
        actions={
          <Stack
            direction="row"
            spacing={2}
            width="100%"
            className="applicant-note-modal__actions"
          >
            <UIButton
              variant="outlined"
              color="inherit"
              fullWidth
              onClick={handleModalClose}
            >
              {shared.cancel}
            </UIButton>
            <UIButton
              appearance="gradient"
              fullWidth
              disabled={validate}
              onClick={handleSubmit}
            >
              {shared.submit}
            </UIButton>
          </Stack>
        }
      >
        <Note
          validate={validate}
          setValidate={setValidate}
          handleDelete={(id) => handleDeleteLocation(id)}
          handleSubmit={handleSubmit}
          setOtherValue={setOtherValue}
          otherValue={otherValue}
          selectValue={selectValue}
          setSelectValue={setSelectValue}
          location={location}
        />
      </UIModal>
      <UIActionModal
        open={approvedModalClose}
        title={requests.listRequests.approvedRequest}
        onClose={handleApprovedCloseModal}
        maxWidth="sm"
        PaperProps={{ className: "requests-approve-modal" }}
        actions={
          <UIActionModal.Actions
            cancelLabel={shared.cancel}
            confirmLabel={shared.submit}
            onCancel={handleApprovedCloseModal}
            onConfirm={handleApprovedBulkRequest}
          />
        }
      >
        <p className="ui-action-modal__body-text requests-approve-modal__description">
          {requests.listRequests.areYouSure}
        </p>
      </UIActionModal>
    </div>
  );
};
RequestsTable.propTypes = {
  isApplicant: PropTypes.bool,
  isInviteApplicants: PropTypes.bool,
  headlines: PropTypes.array,
  breadcrumbsList: PropTypes.array,
  userId: PropTypes.number,
  searchPlaceholder: PropTypes.string,
};

RequestsTable.defaultProps = { isApplicant: false };

export default RequestsTable;
